import { useEffect, useMemo, useState } from "react";
import api, { apiError, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tag, Plus, Trash, CircleNotch, Package, MagnifyingGlass, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const prodImg = (p) => {
  const id = (p.gallery_asset_ids || [])[0] || p.base_asset_id || (p.reference_asset_ids || [])[0];
  return id ? assetUrl(`/api/assets/${id}`) : null;
};

export default function Promotions() {
  const [promos, setPromos] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", percent: 10, active: true, product_ids: [] });
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, pd] = await Promise.all([api.get("/promotions"), api.get("/products")]);
      setPromos(pr.data || []);
      setProducts((pd.data || []).filter((p) => p.status === "published" || p.status === "draft"));
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const prodName = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p.name])), [products]);

  const openNew = () => { setEditing(null); setForm({ name: "", percent: 10, active: true, product_ids: [] }); setSearch(""); setOpen(true); };
  const openEdit = (pr) => { setEditing(pr); setForm({ name: pr.name, percent: pr.percent, active: pr.active, product_ids: [...(pr.product_ids || [])] }); setSearch(""); setOpen(true); };

  const toggleProduct = (id) => setForm((f) => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Ponle un nombre a la promoción."); return; }
    if (!form.percent || form.percent <= 0) { toast.error("El descuento debe ser mayor a 0%."); return; }
    if (!form.product_ids.length) { toast.error("Elige al menos un producto."); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/promotions/${editing.id}`, form); toast.success("Promoción actualizada"); }
      else { await api.post("/promotions", form); toast.success("Promoción creada"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggleActive = async (pr) => {
    try { await api.put(`/promotions/${pr.id}`, { ...pr, active: !pr.active }); setPromos((ps) => ps.map((x) => x.id === pr.id ? { ...x, active: !x.active } : x)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const del = async (pr) => {
    if (!window.confirm(`¿Eliminar la promoción "${pr.name}"?`)) return;
    try { await api.delete(`/promotions/${pr.id}`); setPromos((ps) => ps.filter((x) => x.id !== pr.id)); toast.success("Promoción eliminada"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = products.filter((p) => !search.trim() || (p.name || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="max-w-5xl mx-auto px-5 py-8" data-testid="promotions-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Tag size={24} weight="duotone" className="text-gold" /> Promociones</h1>
          <p className="text-sm text-neutral-500 mt-1">Crea descuentos por porcentaje, elige los productos y actívalos cuando quieras.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold shrink-0" data-testid="new-promo-btn"><Plus size={16} className="mr-1" /> Nueva promoción</Button>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center"><CircleNotch size={28} className="animate-spin text-gold" /></div>
      ) : promos.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 py-20 text-center text-neutral-500" data-testid="promos-empty">
          <Tag size={38} weight="duotone" className="mx-auto mb-3 text-neutral-600" />
          <p>Aún no tienes promociones. Crea una para poner productos en oferta.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="promos-list">
          {promos.map((pr) => (
            <div key={pr.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-4" data-testid={`promo-row-${pr.id}`}>
              <div className="w-14 h-14 rounded-xl bg-gold/15 text-gold flex flex-col items-center justify-center font-bold shrink-0">
                <span className="text-lg leading-none">{Math.round(pr.percent)}%</span>
                <span className="text-[9px] uppercase tracking-wide opacity-70">dcto</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold truncate">{pr.name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${pr.active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-neutral-400"}`}>{pr.active ? "Activa" : "Pausada"}</span>
                </div>
                <p className="text-xs text-neutral-500 mt-0.5 truncate">{(pr.product_ids || []).length} producto(s): {(pr.product_ids || []).slice(0, 3).map((id) => prodName[id]).filter(Boolean).join(", ")}{(pr.product_ids || []).length > 3 ? "…" : ""}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="flex items-center gap-2"><span className="text-xs text-neutral-400">Activa</span><Switch checked={pr.active} onCheckedChange={() => toggleActive(pr)} data-testid={`promo-toggle-${pr.id}`} /></div>
                <Button variant="outline" size="sm" className="rounded-full border-white/15" onClick={() => openEdit(pr)} data-testid={`promo-edit-${pr.id}`}>Editar</Button>
                <button onClick={() => del(pr)} className="text-rose-400 hover:text-rose-300 p-2" data-testid={`promo-delete-${pr.id}`}><Trash size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-[#141414] border-white/15 text-neutral-100" data-testid="promo-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar promoción" : "Nueva promoción"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-neutral-400">Nombre</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Día de las Madres" className="mt-1 bg-white/5 border-white/15" data-testid="promo-name-input" />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Descuento %</Label>
                <Input type="number" min="1" max="95" value={form.percent} onChange={(e) => setForm({ ...form, percent: Number(e.target.value) })} className="mt-1 bg-white/5 border-white/15" data-testid="promo-percent-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-neutral-300"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="promo-active-switch" /> Activa ahora</label>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-neutral-400">Productos en esta promoción ({form.product_ids.length})</Label>
                {form.product_ids.length > 0 && <button onClick={() => setForm({ ...form, product_ids: [] })} className="text-[11px] text-neutral-500 hover:text-white">Quitar todos</button>}
              </div>
              <div className="relative mb-2">
                <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar producto…" className="pl-8 h-9 bg-white/5 border-white/15" data-testid="promo-search-input" />
              </div>
              <div className="max-h-72 overflow-y-auto rounded-xl border border-white/10 divide-y divide-white/5" data-testid="promo-product-list">
                {filtered.length === 0 && <p className="text-xs text-neutral-500 p-4 text-center">No hay productos.</p>}
                {filtered.map((p) => {
                  const sel = form.product_ids.includes(p.id);
                  const img = prodImg(p);
                  return (
                    <button key={p.id} onClick={() => toggleProduct(p.id)} data-testid={`promo-product-${p.id}`} className={`w-full flex items-center gap-3 p-2.5 text-left transition-colors ${sel ? "bg-gold/10" : "hover:bg-white/5"}`}>
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/5 flex items-center justify-center shrink-0">{img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <Package size={16} className="text-neutral-600" />}</div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm truncate">{p.name}</p>
                        <p className="text-[11px] text-neutral-500">{p.price > 0 ? `$${Number(p.price).toLocaleString()}` : "Personalizable"}{form.percent > 0 && p.price > 0 && sel ? ` → $${(p.price * (1 - form.percent / 100)).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : ""}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${sel ? "bg-gold border-gold text-obsidian" : "border-white/25"}`}>{sel && <X size={12} weight="bold" className="rotate-45" />}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="rounded-full border-white/15" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="promo-save-btn">{saving ? "…" : (editing ? "Guardar cambios" : "Crear promoción")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
